<template>
  <ul class="list-wrapper" v-if="rateList.length">
    <li
      class="list-item"
      v-for="(rate, index) in rateList"
      :key="index"
      :class="{ selected: selectedRateIndex === index }"
      @click="setSelectedRate(index)"
    >
      <p class="rate-type">{{ rate.name }}</p>
      <p>{{ rate.hasMultipleRates ? $t('rateFinder.fromAmount', { amount: rate.amount }) : rate.amount }}</p>
    </li>
  </ul>
</template>

<script lang="ts">
import { defineComponent, computed, watch, ref, PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import { groupBy, omit, flatten } from 'lodash';
import { formatCurrency } from '@/utils/helpers';
import { CountryRate, RateListItem, Rate, RateCategory } from '@/types/rates';

export default defineComponent({
  name: 'RateListCard',
  props: {
    selectedCountryRates: Object as PropType<CountryRate | null>,
    currency: String,
  },
  emits: ['update:selectRate'],
  setup(props, { emit }) {
    const i18n = useI18n();
    const { t } = i18n;

    const selectedRateIndex = ref<number>(-1);

    watch(
      () => props.selectedCountryRates,
      () => {
        selectedRateIndex.value = -1;
      },
      { immediate: true },
    );

    const rateList = computed<RateListItem[]>(() => {
      if (!props.selectedCountryRates) return [];

      const { landline = [], mobile = [], premium = [] } = props.selectedCountryRates;

      const groupRates = (rates: Rate[], type: RateCategory) => {
        const rateTypeLabel = t(`rateFinder.rateType.${type}`);
        const grouped = groupBy(rates, (item) => item.network ?? 'unknown');

        const unknownRates = grouped.unknown ?? [];
        const knownRates = flatten(Object.values(omit(grouped, 'unknown')));

        const result: RateListItem[] = [];

        result.push(
          ...knownRates
            .sort((a, b) => a.rate - b.rate)
            .map(({ rate, network }) => ({
              name: `${rateTypeLabel} - ${network}`,
              amount: formatCurrency(rate, props.currency),
              hasMultipleRates: false,
              type,
            })),
        );

        if (unknownRates.length) {
          const minRate = Math.min(...unknownRates.map(({ rate }) => rate));
          const name = knownRates.length > 0 ? `${rateTypeLabel} - ${t('rateFinder.others')}` : rateTypeLabel;

          result.push({
            name,
            amount: formatCurrency(minRate, props.currency),
            hasMultipleRates: unknownRates.length > 1,
            type,
          });
        }

        return result;
      };

      return [...groupRates(mobile, 'mobile'), ...groupRates(landline, 'landline'), ...groupRates(premium, 'premium')];
    });
    const setSelectedRate = (index: number) => {
      if (selectedRateIndex.value === index) {
        selectedRateIndex.value = -1;
        emit('update:selectRate', null);
        return;
      }
      selectedRateIndex.value = index;
      const rate: RateListItem = rateList.value[index];
      emit('update:selectRate', rate);
    };

    return {
      selectedRateIndex,
      rateList,
      setSelectedRate,
    };
  },
});
</script>

<style lang="scss" scoped>
.list-wrapper {
  padding: 0;
  margin-top: 0;
  margin-bottom: 0;
  border: 1px solid $color-soap;
  border-radius: 0.5rem;
  overflow: hidden;
  p {
    margin: 0;
    font-size: 0.875rem;
    color: $color-space-blue;
  }
}

.list-item {
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  cursor: pointer;

  &.selected {
    background-color: $color-water;
  }

  &:not(:last-of-type) {
    border-bottom: 1px solid $color-soap;
  }
}

.rate-type {
  margin-right: auto !important;
}
</style>
